import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useCorporateInteriorDesign } from "../useHook";
import { ImageWithFallback } from "../../../Utils/loader";
import Swal from "sweetalert2";
import Loader from "../../../Utils/loader";

const CorporateInteriorDesignDetails = () => {
  const { id } = useParams();
  const { getCorporateInteriorDesignById } = useCorporateInteriorDesign();
  const [interiorData, setInteriorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");

  useEffect(() => {
    loadCorporateInteriorDesignDetails();
  }, [id]);

  const loadCorporateInteriorDesignDetails = async () => {
    try {
      setLoading(true);
      await getCorporateInteriorDesignById(id, setInteriorData);
    } catch (error) {
      console.error("Error loading corporate interior design details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load corporate interior design details.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader message="Loading corporate interior design details..." />;
  }

  if (!interiorData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">
            Corporate Interior Design Not Found
          </h2>
          <p className="text-gray-500">
            The corporate interior design you're looking for doesn't exist.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-100 p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold mb-1">Corporate Interior Design Details</h2>
        </div>
      </div>

      <div className="max-w-7xl mx-auto my-6 p-6 bg-white shadow-lg rounded-lg">
        <div className="mb-6">
          <h2 className="text-3xl font-bold">{interiorData.title}</h2>
          <p className="text-gray-600 mt-1">
            Category: {interiorData.category?.name}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Created: {new Date(interiorData.created_at).toLocaleDateString()}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="mb-2">
              <span className="font-semibold">Room Size: </span>
              {interiorData.room_size}
            </p>
            <p className="mb-2">
              <span className="font-semibold">Design Type: </span>
              {interiorData.design_type}
            </p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex border-b border-gray-300 mb-4">
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "description"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>
            <button
              className={`py-2 px-4 font-medium focus:outline-none ${
                activeTab === "images"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
              onClick={() => setActiveTab("images")}
            >
              Images
            </button>
          </div>

          {activeTab === "description" && (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: interiorData.description }}
            />
          )}

          {activeTab === "images" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {interiorData.images?.map((img, index) => (
                <div key={img.id || index} className="relative">
                  <ImageWithFallback
                    src={img.image_url}
                    alt={`Corporate Interior Design ${index + 1}`}
                    className="w-full h-48 object-cover rounded shadow-md"
                  />
                  {img.is_primary && (
                    <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                      Primary
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CorporateInteriorDesignDetails;
